// src/components/AlertsPanel.js

import { useState, useMemo, useEffect } from 'react';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  ThermometerSun,
  Clock,
  X,
  ChevronRight,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// Alert type configurations
const ALERT_TYPES = {
  critical: {
    icon: AlertCircle,
    color: 'danger',
    label: 'Critical',
    priority: 1,
  },
  warning: {
    icon: AlertTriangle,
    color: 'warning',
    label: 'Warning',
    priority: 2,
  },
  info: {
    icon: ThermometerSun,
    color: 'info',
    label: 'Info',
    priority: 3,
  },
  success: {
    icon: CheckCircle,
    color: 'success',
    label: 'Resolved',
    priority: 4,
  },
};

// Generate alerts from shipments and excursions
function generateAlerts(shipments, excursions) {
  const alerts = [];
  const now = new Date();

  // Critical excursion alerts
  excursions
    .filter(e => e.severity === 'Critical')
    .slice(0, 5)
    .forEach((exc, i) => {
      const shipment = shipments.find(s => s.id === exc.shipmentId);
      alerts.push({
        id: `critical-${exc.id}`,
        type: 'critical',
        title: 'Critical Temperature Excursion',
        message: `${shipment?.productName || 'Shipment'} exceeded limits by +${exc.maxDeviationC}°C`,
        shipmentId: exc.shipmentId,
        timestamp: new Date(now - (i * 15 + Math.random() * 10) * 60000), // Staggered times
        rootCause: exc.rootCause,
        valueAtRisk: exc.valueAtRisk,
        isNew: i < 2,
      });
    });

  // Major excursion alerts
  excursions
    .filter(e => e.severity === 'Major')
    .slice(0, 3)
    .forEach((exc, i) => {
      const shipment = shipments.find(s => s.id === exc.shipmentId);
      alerts.push({
        id: `warning-${exc.id}`,
        type: 'warning',
        title: 'Temperature Warning',
        message: `${shipment?.productName || 'Shipment'} approaching stability limit`,
        shipmentId: exc.shipmentId,
        timestamp: new Date(now - (30 + i * 20 + Math.random() * 15) * 60000),
        rootCause: exc.rootCause,
        isNew: false,
      });
    });

  // High stability budget usage alerts
  shipments
    .filter(s => s.stabilityBudgetUsed > 75 && s.status === 'In Transit')
    .slice(0, 3)
    .forEach((s, i) => {
      alerts.push({
        id: `stability-${s.id}`,
        type: 'warning',
        title: 'High Stability Budget Usage',
        message: `${s.productName} at ${s.stabilityBudgetUsed?.toFixed(0)}% stability budget`,
        shipmentId: s.id,
        timestamp: new Date(now - (45 + i * 25) * 60000),
        isNew: false,
      });
    });

  // Delivery success alerts
  shipments
    .filter(s => s.status === 'Delivered' && !s.hasExcursion)
    .slice(0, 2)
    .forEach((s, i) => {
      alerts.push({
        id: `delivered-${s.id}`,
        type: 'success',
        title: 'Shipment Delivered Successfully',
        message: `${s.productName} arrived within temperature limits`,
        shipmentId: s.id,
        timestamp: new Date(now - (60 + i * 30) * 60000),
        isNew: false,
      });
    });

  // Long transit alerts
  shipments
    .filter(s => s.status === 'In Transit' && s.transitHours > 60)
    .slice(0, 2)
    .forEach((s, i) => {
      alerts.push({
        id: `transit-${s.id}`,
        type: 'info',
        title: 'Extended Transit Time',
        message: `${s.productName} in transit for ${s.transitHours} hours`,
        shipmentId: s.id,
        timestamp: new Date(now - (90 + i * 45) * 60000),
        isNew: false,
      });
    });

  // Sort by priority then timestamp
  return alerts.sort((a, b) => {
    const priorityDiff = ALERT_TYPES[a.type].priority - ALERT_TYPES[b.type].priority;
    if (priorityDiff !== 0) return priorityDiff;
    return b.timestamp - a.timestamp;
  });
}

// Format relative time
function formatRelativeTime(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
}

// Single Alert Item
function AlertItem({ alert, onDismiss, onView, colors }) {
  const config = ALERT_TYPES[alert.type];
  const Icon = config.icon;
  const alertColor = colors[config.color];
  const alertBg = colors[`${config.color}Bg`];

  return (
    <div
      style={{
        background: colors.bgElevated,
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '0.75rem',
        borderLeft: `4px solid ${alertColor}`,
        position: 'relative',
        animation: alert.isNew ? 'slideIn 0.3s ease' : 'none',
      }}
    >
      {/* New badge */}
      {alert.isNew && (
        <span
          style={{
            position: 'absolute',
            top: '0.75rem',
            right: '2.5rem',
            background: alertColor,
            color: 'white',
            fontSize: '0.6rem',
            fontWeight: 700,
            padding: '0.15rem 0.4rem',
            borderRadius: '4px',
            textTransform: 'uppercase',
          }}
        >
          New
        </span>
      )}

      {/* Dismiss button */}
      <button
        onClick={() => onDismiss(alert.id)}
        style={{
          position: 'absolute',
          top: '0.75rem',
          right: '0.75rem',
          background: 'transparent',
          border: 'none',
          color: colors.textMuted,
          cursor: 'pointer',
          padding: '0.25rem',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = colors.bgHover || colors.bgCard;
          e.currentTarget.style.color = colors.textPrimary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = colors.textMuted;
        }}
      >
        <X size={14} />
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: alertBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={18} color={alertColor} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.25rem',
            }}
          >
            <span
              style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: colors.textPrimary,
              }}
            >
              {alert.title}
            </span>
          </div>

          <p
            style={{
              fontSize: '0.8rem',
              color: colors.textSecondary,
              margin: '0 0 0.5rem 0',
              lineHeight: 1.4,
            }}
          >
            {alert.message}
          </p>

          {/* Meta info */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '0.5rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span
                style={{
                  fontSize: '0.7rem',
                  color: colors.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}
              >
                <Clock size={12} />
                {formatRelativeTime(alert.timestamp)}
              </span>

              {alert.valueAtRisk && (
                <span
                  style={{
                    fontSize: '0.7rem',
                    color: colors.danger,
                    fontWeight: 600,
                  }}
                >
                  ${alert.valueAtRisk.toLocaleString()} at risk
                </span>
              )}
            </div>

            <button
              onClick={() => onView(alert.shipmentId)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                background: 'transparent',
                border: 'none',
                color: colors.primary,
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: 'pointer',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.primaryBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              View Details
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Alerts Panel
function AlertsPanel({ shipments, excursions, onSelectShipment }) {
  const { colors } = useTheme();
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'critical', 'warning', 'info', 'success'
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Generate alerts
  const allAlerts = useMemo(() => {
    return generateAlerts(shipments, excursions);
  }, [shipments, excursions]);

  // Filter alerts
  const visibleAlerts = useMemo(() => {
    return allAlerts
      .filter(a => !dismissedAlerts.includes(a.id))
      .filter(a => filter === 'all' || a.type === filter);
  }, [allAlerts, dismissedAlerts, filter]);

  // Count by type
  const alertCounts = useMemo(() => {
    const counts = { all: 0, critical: 0, warning: 0, info: 0, success: 0 };
    allAlerts
      .filter(a => !dismissedAlerts.includes(a.id))
      .forEach(a => {
        counts.all++;
        counts[a.type]++;
      });
    return counts;
  }, [allAlerts, dismissedAlerts]);

  const handleDismiss = (alertId) => {
    setDismissedAlerts(prev => [...prev, alertId]);
  };

  const handleDismissAll = () => {
    setDismissedAlerts(allAlerts.map(a => a.id));
  };

  const handleView = (shipmentId) => {
    const shipment = shipments.find(s => s.id === shipmentId);
    if (shipment && onSelectShipment) {
      onSelectShipment(shipment);
    }
  };

  const FilterButton = ({ type, label, count }) => {
    const isActive = filter === type;
    const typeColor = type === 'all' ? colors.primary : colors[ALERT_TYPES[type]?.color] || colors.primary;

    return (
      <button
        onClick={() => setFilter(type)}
        style={{
          padding: '0.4rem 0.75rem',
          background: isActive ? `${typeColor}20` : 'transparent',
          border: `1px solid ${isActive ? typeColor : colors.border}`,
          borderRadius: '6px',
          color: isActive ? typeColor : colors.textMuted,
          fontSize: '0.75rem',
          fontWeight: 500,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          transition: 'all 0.2s ease',
        }}
      >
        {label}
        {count > 0 && (
          <span
            style={{
              background: isActive ? typeColor : colors.bgElevated,
              color: isActive ? 'white' : colors.textMuted,
              fontSize: '0.65rem',
              fontWeight: 600,
              padding: '0.1rem 0.4rem',
              borderRadius: '4px',
              minWidth: '18px',
              textAlign: 'center',
            }}
          >
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '1rem 1.25rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Bell size={18} color={colors.warning} />
          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: colors.textPrimary }}>
            Alerts
          </span>
          {alertCounts.critical > 0 && (
            <span
              style={{
                background: colors.danger,
                color: 'white',
                fontSize: '0.65rem',
                fontWeight: 700,
                padding: '0.2rem 0.5rem',
                borderRadius: '10px',
                animation: 'pulse 2s infinite',
              }}
            >
              {alertCounts.critical} Critical
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* Sound toggle */}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            style={{
              background: 'transparent',
              border: 'none',
              color: soundEnabled ? colors.primary : colors.textMuted,
              cursor: 'pointer',
              padding: '0.35rem',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={soundEnabled ? 'Mute alerts' : 'Unmute alerts'}
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>

          {/* Clear all */}
          {visibleAlerts.length > 0 && (
            <button
              onClick={handleDismissAll}
              style={{
                background: 'transparent',
                border: `1px solid ${colors.border}`,
                color: colors.textMuted,
                fontSize: '0.7rem',
                padding: '0.35rem 0.6rem',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.danger;
                e.currentTarget.style.color = colors.danger;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
                e.currentTarget.style.color = colors.textMuted;
              }}
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div
        style={{
          padding: '0.75rem 1.25rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          background: colors.bgSecondary,
        }}
      >
        <FilterButton type="all" label="All" count={alertCounts.all} />
        <FilterButton type="critical" label="Critical" count={alertCounts.critical} />
        <FilterButton type="warning" label="Warning" count={alertCounts.warning} />
        <FilterButton type="info" label="Info" count={alertCounts.info} />
        <FilterButton type="success" label="Resolved" count={alertCounts.success} />
      </div>

      {/* Alerts List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1rem 1.25rem',
        }}
      >
        {visibleAlerts.length > 0 ? (
          visibleAlerts.map(alert => (
            <AlertItem
              key={alert.id}
              alert={alert}
              onDismiss={handleDismiss}
              onView={handleView}
              colors={colors}
            />
          ))
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '3rem 1rem',
              color: colors.textMuted,
            }}
          >
            <CheckCircle size={40} color={colors.success} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <div style={{ fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.25rem' }}>
              All clear!
            </div>
            <div style={{ fontSize: '0.8rem' }}>
              No {filter !== 'all' ? filter : ''} alerts to display
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '0.75rem 1.25rem',
          borderTop: `1px solid ${colors.border}`,
          background: colors.bgSecondary,
          fontSize: '0.7rem',
          color: colors.textMuted,
          textAlign: 'center',
        }}
      >
        Showing {visibleAlerts.length} of {allAlerts.length} alerts
      </div>

      {/* CSS Animation */}
      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(-10px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }
        `}
      </style>
    </div>
  );
}

export default AlertsPanel;